import {
  DateFormat,
  NullableDateInput,
  stringifyDate,
} from '@superdispatch/dates';
import { CUSTOMER_TYPES, VEHICLE_TYPES } from '@superdispatch/sdk';
import { PricingInsightsDTO } from 'shared/modules/pricing-insights/PricingInsightsDTO';
import { BUSINESS_HOURS_TYPES } from 'shared/utils/businessHoursDisplay';
import { businessHoursRowSchema } from 'shared/data/BusinessHoursDTO';
import { z } from 'zod';
import {
  LOAD_INSPECTION_TYPES,
  OFFER_DECLINE_REASONS,
} from '../../../loads/data/LoadDTO';
import { OFFER_SOURCES } from '../../core/OfferSourceEnum';
import { OFFER_STATUSES } from '../../core/OffersStatusEnum';

const zodString = z
  .string()
  .nullish()
  .transform((value) => value ?? '');

const zodBoolean = z.preprocess(
  (value) =>
    typeof value === 'string' || typeof value === 'number'
      ? /^(true|1)$/i.test(String(value))
      : value,
  z.boolean().nullable().default(false).catch(false),
);

const zodNumber = z.preprocess(
  // `Number('')` is `0`, which would turn a cleared numeric input into a value.
  (value) => (value == null || value === '' ? null : Number(value)),
  z.number().nullable().default(null).catch(null),
);

/**
 * Same as {@link zodNumber}, but additionally parses numeric strings with
 * thousand separators (e.g. `"1,234.5"`).
 */
const zodFloat = z.preprocess((value) => {
  if (typeof value === 'string') {
    const parsed = parseFloat(value.replace(/,/g, ''));
    return Number.isFinite(parsed) ? parsed : null;
  }

  return value;
}, zodNumber);

function zodDateString(format: DateFormat) {
  return z.preprocess(
    (value) => stringifyDate(value as NullableDateInput, { format }),
    z.string().nullable().default(null),
  );
}

export type OfferVehicleDTO = z.infer<typeof offerVehiclesSchema>;
const offerVehiclesSchema = z.object({
  make: zodString,
  model: zodString,
  year: zodNumber,
  type: z.enum(VEHICLE_TYPES).nullable().catch(null).default(null),

  is_inoperable: zodBoolean,
  requires_enclosed_trailer: zodBoolean,

  curb_weight: zodFloat,
  curb_weight_unit: zodString,

  height: zodNumber,
  length: zodNumber,
  width: zodNumber,

  sample_photo: z
    .object({
      guid: zodString,
      photo_url: zodString,
      thumbnail_url: zodString,
    })
    .nullable()
    .catch(null)
    .default(null),
});

export type OfferDTO = z.infer<typeof offerSchema>;
export const offerSchema = z.object({
  guid: zodString,
  source: z.enum(OFFER_SOURCES).nullable().catch(null).default(null),
  created_at: zodDateString('DateTimeISO'),
  is_counter_offer: zodBoolean,
  bid_price: zodNumber,
  original_price: zodNumber,
  carrier_contact_name: zodString,
  load: z
    .object({
      load_id: zodString,
      broker_name: zodString,
      broker_guid: zodString,
      payment_method_display: zodString,
      payment_terms_display: zodString,
      price: zodNumber,
      broker_fee: zodNumber,
      price_per_mile: zodNumber,
      distance_miles: zodNumber,

      is_inoperable: zodBoolean,
      requires_enclosed_trailer: zodBoolean,

      pickup_city: zodString,
      pickup_state: zodString,
      pickup_zip: zodString,
      pickup_date: zodDateString('DateTimeISO'),
      pickup_business_type: z
        .enum(CUSTOMER_TYPES)
        .nullable()
        .catch(null)
        .default(null),

      delivery_city: zodString,
      delivery_state: zodString,
      delivery_zip: zodString,
      delivery_date: zodDateString('DateTimeISO'),
      delivery_business_type: z
        .enum(CUSTOMER_TYPES)
        .nullable()
        .catch(null)
        .default(null),

      vehicles: z.array(offerVehiclesSchema).prefault([]),
    })
    .nullable()
    .prefault({}),
});

export type OfferDetailsDTO = z.infer<typeof offerDetailsSchema>;
export const offerDetailsSchema = z.object({
  guid: zodString,
  status: z.enum(OFFER_STATUSES).catch(OFFER_STATUSES[0]),
  source: z.enum(OFFER_SOURCES).nullable().catch(null).default(null),
  created_at: zodDateString('DateTimeISO'),
  status_changed_at: zodDateString('DateTimeISO'),
  is_counter_offer: zodBoolean,
  is_price_negotiation: zodBoolean,
  bid_price: zodNumber,
  original_price: zodNumber,
  carrier_contact_name: zodString,
  is_subhauling_offer: zodBoolean,
  load: z.object({
    guid: zodString,
    load_id: zodString,
    inspection_type: z
      .enum(LOAD_INSPECTION_TYPES)
      .catch(LOAD_INSPECTION_TYPES[0]),
    shipper_order_guid: zodString,

    pickup_city: zodString,
    pickup_state: zodString,
    pickup_zip: zodString,
    pickup_date: zodDateString('DateTimeISO'),
    pickup_date_type: zodString,
    pickup_business_type: z
      .enum(CUSTOMER_TYPES)
      .nullable()
      .catch(null)
      .default(null),
    pickup_business_hours_type: z
      .enum(BUSINESS_HOURS_TYPES)
      .nullable()
      .catch(null)
      .default(null),
    pickup_business_hours: z.array(businessHoursRowSchema).prefault([]),

    delivery_city: zodString,
    delivery_state: zodString,
    delivery_zip: zodString,
    delivery_date: zodDateString('DateTimeISO'),
    delivery_date_type: zodString,
    delivery_business_type: z
      .enum(CUSTOMER_TYPES)
      .nullable()
      .catch(null)
      .default(null),
    delivery_business_hours_type: z
      .enum(BUSINESS_HOURS_TYPES)
      .nullable()
      .catch(null)
      .default(null),
    delivery_business_hours: z.array(businessHoursRowSchema).prefault([]),

    shipper_guid: zodString,
    shipper_name: zodString,
    shipper_phone: zodString,
    shipper_profile_url: zodString,
    shipper_is_verified: z.boolean().nullable().catch(null).default(null),

    instructions: zodString,
    terms_and_conditions: zodString,

    is_inoperable: zodBoolean,
    requires_enclosed_trailer: zodBoolean,

    price: zodNumber,
    broker_fee: zodNumber,
    price_per_mile: zodNumber,
    distance_miles: zodNumber,

    payment_method_display: zodString,
    payment_terms_display: zodString,

    vehicles: z.array(offerVehiclesSchema).prefault([]),
  }),
});

export const offerCountDataSchema = z.object({
  count: zodNumber,
  timestamp: zodDateString('DateTimeISO'),
});

export const counterOfferNotificationSchema = z.object({
  timestamp: zodDateString('DateTimeISO'),
  counter_offer: z.object({
    guid: zodString,
    load: z.object({
      pickup_state: zodString,
      delivery_state: zodString,
      price: zodNumber,
    }),
  }),
});

export type OfferDeclinePayloadDTO = z.infer<typeof offerDeclinePayloadSchema>;
export const offerDeclinePayloadSchema = z.object({
  decline_comment: z
    .string()
    .max(512, 'Maximum length is 512 characters')
    .prefault(''),
  decline_reasons: z.array(z.enum(OFFER_DECLINE_REASONS)).prefault([]),
});

export function mapOfferLoadToPricingInsights(
  offer: OfferDetailsDTO['load'],
): PricingInsightsDTO {
  return {
    origin: {
      zip: offer.pickup_zip,
      city: offer.pickup_city,
      state: offer.pickup_state,
    },

    destination: {
      zip: offer.delivery_zip,
      city: offer.delivery_city,
      state: offer.delivery_state,
    },

    vehicles: offer.vehicles?.map((vehicle) => ({
      year: vehicle.year,
      type: vehicle.type,
      make: vehicle.make,
      model: vehicle.model,
      is_inoperable: vehicle.is_inoperable,
    })),
  };
}
